import { useEffect, useRef, useState } from 'react';
import { ROLES } from '../../config/roles';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useUserContext } from '../../context/user';
import { useAuthContext } from '../../context/auth';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import AvatarEditor from '../profile/AvatarEditor';
import { avatarColor, avatarSrc, getInitials } from '../../utils/avatar';

const validator = require('validator');

const Edit = ({ user, units }) => {
  const axiosPrivate = useAxiosPrivate();
  const { dispatch } = useUserContext();
  const { auth } = useAuthContext();
  const theme = useTheme();

  const [error, setError] = useState(null);
  const [show, setShow] = useState(false);
  const [changeIcon, setChangeIcon] = useState(false);

  const nameRef = useRef('');
  const emailRef = useRef('');
  const passwordRef = useRef('');
  const deviceIdRef = useRef('');
  const activeRef = useRef(false);

  const [selectedRoles, setSelectedRoles] = useState(user?.roles?.[0] || '');
  const [selectedCommand, setSelectedCommand] = useState('');
  const [selectedRegiment, setSelectedRegiment] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedOutpost, setSelectedOutpost] = useState('');
  const [commands, setCommands] = useState([]);
  const [regiments, setRegiments] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [outspots, setOutposts] = useState([]);

  const isRootUser = Boolean(user?.roles?.includes(ROLES.Root));
  const permitEditRole = auth?.roles?.includes(ROLES.Root) && !isRootUser;
  const permiEditActive = permitEditRole || user?.roles?.includes(ROLES.User);

  // ---------- Theme (same look as the group dialogs in Chat.jsx) ----------
  const isDark = theme.palette.mode === 'dark';
  const accent = theme.palette.secondary.main;
  const accentText = theme.palette.getContrastText(accent);

  const dialogPaperSx = {
    backgroundColor: theme.palette.background.alt,
    color: theme.palette.text.primary,
    borderRadius: 2,
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : theme.palette.neutral[200]}`,
    boxShadow: 8,
    overflow: 'hidden',
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      color: theme.palette.text.primary,
      backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : theme.palette.background.default,
      '& fieldset': { borderColor: isDark ? 'rgba(255,255,255,0.28)' : theme.palette.neutral[300] },
      '&:hover fieldset': { borderColor: isDark ? 'rgba(255,255,255,0.5)' : theme.palette.neutral[400] },
      '&.Mui-focused fieldset': { borderColor: accent, borderWidth: 1 },
    },
    '& .MuiInputBase-input::placeholder': { color: theme.palette.text.secondary, opacity: 0.8 },
    '& .MuiInputLabel-root': { color: theme.palette.text.secondary },
    '& .MuiInputLabel-root.Mui-focused': { color: accent },
    '& .MuiSelect-icon': { color: theme.palette.text.secondary },
    // The browser paints autofilled fields blue: keep them in the theme
    '& input:-webkit-autofill': {
      WebkitBoxShadow: `0 0 0 100px ${theme.palette.background.alt} inset`,
      WebkitTextFillColor: theme.palette.text.primary,
      caretColor: theme.palette.text.primary,
    },
  };

  const menuProps = {
    PaperProps: {
      sx: {
        backgroundColor: theme.palette.background.alt,
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : theme.palette.neutral[200]}`,
        maxHeight: 320,
      },
    },
  };

  const goldButtonSx = {
    backgroundColor: accent,
    color: accentText,
    fontWeight: 700,
    borderRadius: 1.5,
    px: 2.5,
    boxShadow: 'none',
    '&:hover': { backgroundColor: theme.palette.secondary.light, boxShadow: 'none' },
  };

  const cancelButtonSx = {
    color: theme.palette.text.primary,
    fontWeight: 600,
    '&:hover': { backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' },
  };

  // ---------- Logic (unchanged) ----------
  const closeDialog = () => {
    setShow(false);
    setError(null);
  };

  const handleShowPassword = () => {
    setChangeIcon(!changeIcon);
  };

  const handleRoleChange = (event) => {
    const value = event.target.value;
    setSelectedRoles(typeof value === 'string' ? value : value?.[0] || '');
  };

  const handleUpdate = async () => {
    const updateUser = {
      name: nameRef.current.value,
      email: emailRef.current.value,
      password: passwordRef.current.value,
      roles: selectedRoles,
      active: activeRef.current.checked,
      commandId: selectedCommand,
      regimentId: selectedRegiment,
      companyId: selectedCompany,
      outpostId: selectedOutpost,
      deviceId: deviceIdRef.current.value,
    };

    const prevUser = [user?.name, user?.email, user?.roles?.[0], user?.active];

    Object.keys(updateUser).forEach((key) => {
      if (
        validator.isEmpty(updateUser[key]?.toString() || '', { ignore_whitespace: true }) ||
        prevUser.includes(updateUser[key])
      ) {
        delete updateUser[key];
      }
    });

    if (!auth) {
      setError('You must be logged in');
      return;
    }

    const checkChange = Object.keys(updateUser).length === 0;

    if (!checkChange) {
      updateUser.id = user._id;

      try {
        const response = await axiosPrivate.patch('/api/users', updateUser);
        dispatch({ type: 'UPDATE_USER', payload: response.data });
        setError(null);
        setShow(false);
      } catch (error) {
        setSelectedRoles(user?.roles?.[0] || '');
        activeRef.current.checked = user?.active;
        setError(error.response?.data.error);
      }
    } else {
      setError('Nothing Changed');
    }
  };

  useEffect(() => {
    if (!user) return;

    setSelectedRoles(user.roles?.[0] || '');

    setCommands(units.filter((unit) => unit.type === 0));
    setRegiments(units.filter((unit) => unit.type === 1));
    setCompanies(units.filter((unit) => unit.type === 2));
    setOutposts(units.filter((unit) => unit.type === 3));

    setSelectedCommand(user.commandId);
    setSelectedRegiment(user.regimentId);
    setSelectedCompany(user.companyId);
    setSelectedOutpost(user.outpostId);
  }, [user, units]);

  // One unit drop-down (a function, not a component: a component defined here would restart on every render)
  const renderUnitSelect = (label, value, setValue, items) => (
    <FormControl fullWidth sx={fieldSx}>
      <InputLabel>{label}</InputLabel>
      <Select label={label} value={value || '0'} onChange={(e) => setValue(e.target.value)} MenuProps={menuProps}>
        <MenuItem value="0">All</MenuItem>
        {items?.map((item) => (
          <MenuItem key={item._id} value={item._id}>
            {item.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <>
      <Tooltip title="Edit user">
        <IconButton color="secondary" size="small" style={{ marginRight: 4 }} onClick={() => setShow(true)}>
          <EditIcon />
        </IconButton>
      </Tooltip>

      <Dialog open={show} onClose={closeDialog} fullWidth maxWidth="md" PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle
          sx={{
            px: 3,
            py: 2,
            fontWeight: 600,
            color: theme.palette.text.primary,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          Edit user details
        </DialogTitle>

        <DialogContent
          sx={{
            px: 3,
            pb: 3,
            // '&&' outranks MUI's "DialogTitle + DialogContent { padding-top: 0 }" rule
            '&&': { pt: 3 },
            backgroundColor: theme.palette.background.alt,
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '220px 1fr' },
              gap: 3,
              alignItems: 'stretch',      // the profile card runs as tall as the form
            }}
          >
            {/* ===== Left: profile ===== */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                p: 2.5,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : alpha(accent, 0.05),
              }}
            >
              {isRootUser ? (
                <Avatar
                  src={avatarSrc(user?.avatar)}
                  sx={{
                    width: 160,
                    height: 160,
                    fontSize: '3.6rem',
                    ...avatarColor(user?.name || '', theme.palette.mode),
                    boxShadow: `0 0 0 3px ${alpha(accent, 0.65)}`,
                  }}
                >
                  {getInitials(user?.name || '')}
                </Avatar>
              ) : (
                <AvatarEditor user={user} menu />
              )}

              <Box textAlign="center" width="100%" minWidth={0}>
                <Typography variant="h6" noWrap sx={{ fontWeight: 700 }}>
                  {user?.name}
                </Typography>
                <Typography variant="body2" noWrap color="text.secondary">
                  {user?.email}
                </Typography>
              </Box>

              <Box display="flex" flexWrap="wrap" gap={0.75} justifyContent="center">
                {(user?.roles || []).map((role) => (
                  <Chip
                    key={role}
                    size="small"
                    variant="outlined"
                    label={role}
                    sx={{ color: accent, borderColor: alpha(accent, 0.6), fontWeight: 600 }}
                  />
                ))}
                <Chip
                  size="small"
                  variant="outlined"
                  label={user?.isOnline ? 'Online' : 'Offline'}
                  color={user?.isOnline ? 'success' : 'default'}
                />
              </Box>
            </Box>

            {/* ===== Right: details ===== */}
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
                <TextField
                  fullWidth
                  label="Name"
                  defaultValue={user?.name}
                  inputRef={nameRef}
                  variant="outlined"
                  autoComplete="off"
                  sx={{ ...fieldSx, gridColumn: { sm: permitEditRole ? 'auto' : '1 / -1' } }}
                />

                {permitEditRole && (
                  <FormControl fullWidth sx={fieldSx}>
                    <InputLabel>Role</InputLabel>
                    <Select label="Role" value={selectedRoles} onChange={handleRoleChange} MenuProps={menuProps}>
                      <MenuItem value="User">User</MenuItem>
                      <MenuItem value="Admin">Admin</MenuItem>
                      <MenuItem value="Guest">Guest</MenuItem>
                    </Select>
                  </FormControl>
                )}

                <TextField
                  fullWidth
                  label="Email"
                  defaultValue={user?.email}
                  inputRef={emailRef}
                  variant="outlined"
                  autoComplete="off"
                  sx={fieldSx}
                />

                <TextField
                  fullWidth
                  label="Password"
                  placeholder="Leave empty to keep the current one"
                  type={changeIcon ? 'text' : 'password'}
                  inputRef={passwordRef}
                  defaultValue=""
                  variant="outlined"
                  autoComplete="new-password"          // stops the browser from filling in a saved password
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          onClick={handleShowPassword}
                          aria-label="Show or hide the password"
                          sx={{ color: theme.palette.text.secondary }}
                        >
                          {changeIcon ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={fieldSx}
                />

                {renderUnitSelect('Command', selectedCommand, setSelectedCommand, commands)}
                {renderUnitSelect('Regiment', selectedRegiment, setSelectedRegiment, regiments)}
                {renderUnitSelect('Company', selectedCompany, setSelectedCompany, companies)}
                {renderUnitSelect('Outpost', selectedOutpost, setSelectedOutpost, outspots)}

                <TextField
                  fullWidth
                  label="Device ID"
                  defaultValue={user?.deviceId}
                  inputRef={deviceIdRef}
                  variant="outlined"
                  autoComplete="off"
                  sx={fieldSx}
                />

                {permiEditActive && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        defaultChecked={user?.active}
                        inputRef={activeRef}
                        sx={{ color: theme.palette.text.secondary, '&.Mui-checked': { color: accent } }}
                      />
                    }
                    label="Active"
                    sx={{ color: theme.palette.text.primary, ml: 0.5 }}
                  />
                )}
              </Box>

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 1.75,
            gap: 1,
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.alt,
          }}
        >
          <Button onClick={closeDialog} sx={cancelButtonSx}>
            Cancel
          </Button>
          <Button onClick={handleUpdate} variant="contained" sx={goldButtonSx}>
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Edit;